'use client';
import React from 'react';
import type { ForgeArenaData } from '@/lib/services/forgeArenaService';

interface StreakLeaderboardProps {
  arenaData: ForgeArenaData | null;
  loading: boolean;
}

export default function StreakLeaderboard({ arenaData, loading }: StreakLeaderboardProps) {
  const theoStreak = arenaData?.theo?.currentStreak ?? 0;
  const tracyStreak = arenaData?.tracy?.currentStreak ?? 0;
  const theoBest = arenaData?.theo?.longestStreak ?? 0;
  const tracyBest = arenaData?.tracy?.longestStreak ?? 0;

  const theoLeader = theoStreak >= tracyStreak;

  const players = [
    {
      id: 'streak-theo',
      name: 'THEO',
      streak: theoStreak,
      best: theoBest,
      color: 'var(--theo-color)',
      leader: theoLeader,
    },
    {
      id: 'streak-tracy',
      name: 'TRACY',
      streak: tracyStreak,
      best: tracyBest,
      color: 'var(--tracy-color)',
      leader: !theoLeader,
    },
  ];

  // Build monthly heatmap from checkins
  const today = new Date();
  const monthlyMap: Record<string, number> = {};
  (arenaData?.monthlyCheckins ?? []).forEach((c) => {
    if (!monthlyMap[c.date] || c.score > monthlyMap[c.date]) {
      monthlyMap[c.date] = c.score;
    }
  });

  return (
    <div className="card-forge rounded-xl p-5">
      <div className="text-forge-label mb-4">Streak Leaderboard</div>
      {loading ? (
        <div className="space-y-3 mb-6 animate-pulse">
          {[1, 2].map((i) => (
            <div key={i} className="card-elevated rounded-lg p-4 border border-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-muted" />
                  <div className="h-4 w-16 bg-muted rounded" />
                </div>
                <div className="h-8 w-12 bg-muted rounded" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3 mb-6">
          {players.map((item) => (
            <div
              key={item.id}
              className={`card-elevated rounded-lg p-4 border ${item.leader ? 'border-primary/30' : 'border-border'}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center border-2"
                    style={{ borderColor: item.color }}
                  >
                    <span className="text-sm font-black" style={{ color: item.color }}>
                      {item.name?.[0]}
                    </span>
                  </div>
                  <div>
                    <div className="font-bold text-sm text-foreground tracking-wider">{item.name}</div>
                    {item.leader && <div className="text-2xs text-primary font-bold">STREAK LEADER</div>}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-tabular font-black text-2xl" style={{ color: item.color }}>
                    {item.streak}
                  </div>
                  <div className="text-forge-label">day streak</div>
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Personal Best</span>
                <span className="font-tabular font-bold text-foreground">{item.best} days</span>
              </div>
              <div className="macro-bar-track mt-2">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${item.best > 0 ? Math.min((item.streak / item.best) * 100, 100) : 0}%`,
                    backgroundColor: item.color,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="border-t border-border pt-4">
        <div className="text-forge-label mb-3">Monthly Execution Record</div>
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: 30 }, (_, i) => {
            const d = new Date(today);
            d.setDate(today.getDate() - (29 - i));
            const dateStr = d.toISOString().split('T')[0];
            const score = monthlyMap[dateStr] ?? 0;
            const color =
              score >= 85
                ? 'bg-primary'
                : score >= 70
                ? 'bg-accent'
                : score > 0
                ? 'bg-destructive' :'bg-muted';
            return (
              <div
                key={`cal-${i}`}
                className={`h-5 rounded-sm ${color} opacity-80`}
                title={`${dateStr}: ${score > 0 ? score : 'No data'}`}
              />
            );
          })}
        </div>
        <div className="flex items-center gap-4 mt-2 text-2xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-sm bg-primary inline-block" /> 85+
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-sm bg-accent inline-block" /> 70–84
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-sm bg-destructive inline-block" /> Below 70
          </span>
        </div>
      </div>
    </div>
  );
}